import { FC, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Grid, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import { DateField } from 'src/components/date-field';
import { InputField } from 'src/components/input-field';
import { ConfirmationDialog } from 'src/components/confirmation-dialog';
import { SuccessDialog } from 'src/components/success-dialog';
import { useFormik } from 'formik';
import { useDialog } from 'src/hooks/use-dialog';
import { useNavigate } from 'react-router-dom';
import { getDateFormat, getEndOfDay, getStartOfDay } from 'src/utils/date-format';
import * as Yup from 'yup';
import { channelPartnerApi } from 'src/api/newChannelPartner/channelPartner';
import { dayInMs } from 'src/enums/common';
import { InvitedCpStatus, VerifiedCpStatus } from 'src/enums/channel-partner';
import { ChannelPartner } from 'src/interfaces/new-channel-partner/channelPartner';
import { Roles } from 'src/enums/role-based-auth-guard';
import { useAuth } from 'src/hooks/use-auth';
import { REPAIR_SERVICE_ID } from 'src/constants/repairRequest-constants';
import { EWASTE_SERVICE_ID } from 'src/constants/common-constant';

interface ApprovalCardProps {
  channelPartner: ChannelPartner;
  setLoading: (flag: boolean) => void;
}

export const ApprovalCard: FC<ApprovalCardProps> = (props) => {
  const { channelPartner, setLoading } = props;
  const auth = useAuth();
  const [editContract, setEditContract] = useState<boolean>(false);
  const [mandatoryDocLength, setMandatoryDocLength] = useState<number>();
  const navigate = useNavigate();
  const [resubmitDialogOpen, handleOpenResubmitDialog, handleCloseResubmitDialog] = useDialog();
  const [rejectDialogOpen, handleOpenRejectDialog, handleCloseRejectDialog] = useDialog();
  const [approveDialogOpen, handleOpenApproveDialog, handleCloseApproveDialog] = useDialog();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      startDate: channelPartner.contractDetails
        ? new Date(channelPartner.contractDetails?.startDate) : null,
      endDate: channelPartner.contractDetails
        ? new Date(channelPartner.contractDetails?.endDate) : null,
      remark: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      startDate: Yup.date().typeError('Select contract start date'),
      endDate: Yup.date().typeError('Select contract end date').test(
        'minCheck', 'Must be greater than contract start date', (value, ctx) => value > ctx.parent.startDate
      ),
      remark: Yup.string().required('Remark is required'),
    }),
    onSubmit: async () => {

    }
  });

  const handleApprovalSubmit = async (statusAction) => {
    // update the status for ChannelPartner
    setLoading(true);
    let attributes = {};
    if (statusAction === 'Rejected') {
      attributes = {
        remark: formik.values.remark,
        status: statusAction,
        id: channelPartner.id,
      };
    } else if (statusAction === 'Resubmission') {
      attributes = {
        remark: formik.values.remark,
        status: statusAction,
        id: channelPartner.id,
      };
    } else {
      attributes = {
        contractDetails: {
          startDate: getStartOfDay(new Date(formik.values.startDate)),
          endDate: getEndOfDay(new Date(formik.values.endDate)),
        },
        remark: formik.values.remark,
        status: statusAction,
        id: channelPartner.id,
      };
    }

    await channelPartnerApi.updateChannelPartnerStatus(attributes);
    if (statusAction === 'Active') {
      handleOpenApproveDialog();
      setLoading(false);
    } else {
      handleCloseResubmitDialog();
      handleCloseRejectDialog();
      navigate('/dashboard/channel-partners/main/invited');
    }
  };

  const handleSuccessDialogClose = () => {
    handleCloseApproveDialog();
    navigate('/dashboard/channel-partners/main/invited');
  };

  const isApproveDisabled = ():boolean => {
    if (!channelPartner.bankDetails?.ifscCode || mandatoryDocLength !== 3
        || (channelPartner.services[0]?.id === REPAIR_SERVICE_ID
          && channelPartner?.brandsProducts?.length < 1)
        || (channelPartner.services[0]?.id === EWASTE_SERVICE_ID
          && channelPartner?.dayAreas?.length < 1)
        || channelPartner.serviceAreaIds?.length < 1
        || channelPartner.services?.length < 1) {
      return true;
    }
    return !(formik.isValid && formik.dirty) || formik.isSubmitting;
  };

  useEffect(() => {
    setMandatoryDocLength(channelPartner.documentDetails.filter((item) => item.mandatory).length);
  }, [channelPartner]);

  return (
    channelPartner
      ? (
        <>
          <Card
            variant="outlined"
            sx={{ mt: 1 }}
          >
            <CardHeader
              title="Contract Details"
              action={(
                <>
                  {(channelPartner.status === VerifiedCpStatus.ACTIVE
              || channelPartner.status === VerifiedCpStatus.INACTIVE)
               && (auth.user.scope !== Roles.CHANNEL_PARTNER) && (
               <Button
                 color="primary"
                 size="small"
                 onClick={() => {
                   setEditContract(true);
                 }}
                 disabled={editContract}
                 variant="contained"
               >
                 Edit
               </Button>
                  )}
                </>
          )}
            />
            <CardContent>
              <Grid
                container
                direction="row"
                spacing={1}
              >
                <Grid
                  item
                  md={2}
                  xs={12}
                  sm={12}
                >
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                    sx={{ ml: 1 }}
                    display="contents"
                  >
                    Contract Start Date
                  </Typography>
                </Grid>
                <Grid
                  item
                  md={3}
                  xs={12}
                  sm={12}
                >
                  {(editContract || channelPartner.status === InvitedCpStatus.PENDING)
                    && (auth.user.scope !== Roles.CHANNEL_PARTNER) ? (
                      <DateField
                        error={Boolean(formik.touched.startDate && formik.errors.startDate)}
                        fullWidth
                        helperText={formik.touched.startDate && formik.errors.startDate}
                        name="startDate"
                        onChange={(date) => {
                          formik.setFieldValue(
                            'startDate', date
                          );
                        }}
                        value={formik.values.startDate}
                        minDate={channelPartner.contractDetails?.startDate
                          ? new Date(channelPartner.contractDetails?.startDate)
                          : new Date()}
                      />
                    ) : (
                      <Typography
                        sx={{
                          overflowWrap: 'break-word'
                        }}
                        color="#506176"
                        variant="body2"
                        display="contents"
                      >
                        {channelPartner.contractDetails?.startDate
                          ? getDateFormat(channelPartner.contractDetails?.startDate) : ''}
                      </Typography>
                    )}
                </Grid>
                <Grid
                  item
                  md={2}
                  xs={12}
                  sm={12}
                >
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                    sx={{ ml: 1 }}
                    display="contents"
                  >
                    Contract End Date
                  </Typography>
                </Grid>
                <Grid
                  item
                  md={3}
                  xs={12}
                  sm={12}
                >
                  {(editContract || channelPartner.status === InvitedCpStatus.PENDING)
                    && (auth.user.scope !== Roles.CHANNEL_PARTNER) ? (
                      <DateField
                        error={Boolean(formik.values.startDate && formik.errors.endDate)}
                        fullWidth
                        helperText={formik.values.startDate && formik.errors.endDate}
                        name="endDate"
                        onChange={(date) => formik.setFieldValue(
                          'endDate', date
                        )}
                        value={formik.values.endDate}
                        minDate={formik.values.startDate
                          ? new Date(new Date(formik.values.startDate).getTime() + dayInMs)
                          : new Date(new Date().getTime() + dayInMs)}
                        disabled={!(formik.values.startDate)}
                      />
                    ) : (
                      <Typography
                        sx={{
                          overflowWrap: 'break-word'
                        }}
                        color="#506176"
                        variant="body2"
                        display="contents"
                      >
                        {channelPartner.contractDetails?.endDate
                          ? getDateFormat(channelPartner.contractDetails?.endDate) : ''}
                      </Typography>
                    )}
                </Grid>
                {editContract && (channelPartner.status === VerifiedCpStatus.ACTIVE
            || channelPartner.status === VerifiedCpStatus.INACTIVE)
            && (
            <Grid
              item
              md={2}
              xs={12}
              sm={12}
            >
              <Button
                sx={{
                  ml: 2
                }}
                color="primary"
                onClick={() => {
                  setEditContract(false);
                }}
                variant="outlined"
              >
                Cancel
              </Button>
            </Grid>
            )}
              </Grid>
            </CardContent>
          </Card>
          {(editContract || channelPartner.status === InvitedCpStatus.PENDING)
       && (auth.user.scope !== Roles.CHANNEL_PARTNER) && (
       <Card
         variant="outlined"
         sx={{ mt: 1 }}
       >
         <CardContent
           sx={{ mt: -3 }}
         >
           <Grid
             container
             spacing={1}
             sx={{ mt: 2, mb: 2 }}
           >
             <Grid
               item
               md={2}
               xs={12}
               sm={12}
             >
               <Box>
                 <Typography
                   color="textPrimary"
                   sx={{ ml: 2, mt: 2 }}
                   variant="h6"
                 >
                   Approval
                 </Typography>
               </Box>
             </Grid>
             <Grid
               item
               md={3}
               xs={10}
               sm={10}
             >
               <InputField
                 error={Boolean(formik.touched.remark && formik.errors.remark)}
                 fullWidth
                 helperText={formik.touched.remark && formik.errors.remark}
                 label="Remark"
                 multiline
                 required
                 name="remark"
                 onBlur={formik.handleBlur}
                 onChange={formik.handleChange}
                 placeholder="Add remark"
                 rows={3}
                 value={formik.values.remark}
               />
             </Grid>
             <Box
               sx={{
                 display: 'grid',
                 gap: 5,
                 gridAutoFlow: 'column',
                 mt: 8,
                 ml: 8
               }}
             >
               <Grid
                 item
               >
                 <ConfirmationDialog
                   message="Are you sure you want to resubmit this profile?"
                   onCancel={handleCloseResubmitDialog}
                   onConfirm={() => handleApprovalSubmit('Resubmission')}
                   open={resubmitDialogOpen}
                   title="Are you sure ?"
                   variant="warning"
                 />
                 <Button
                   color="error"
                   size="large"
                   variant="outlined"
                   onClick={() => handleOpenResubmitDialog()}
                   disabled={formik.values.remark === ''}
                 >
                   Resubmit
                 </Button>
               </Grid>
               <Grid
                 item
               >
                 <ConfirmationDialog
                   message="Are you sure you want to reject this profile?"
                   onCancel={handleCloseRejectDialog}
                   onConfirm={() => handleApprovalSubmit('Rejected')}
                   open={rejectDialogOpen}
                   title="Are you sure ?"
                   variant="warning"
                 />
                 <Button
                   color="error"
                   size="large"
                   variant="contained"
                   disabled={formik.values.remark === ''}
                   onClick={() => handleOpenRejectDialog()}
                 >
                   Reject
                 </Button>
               </Grid>
               <Grid
                 item
               >
                 <SuccessDialog
                   message="You have successfully onboarded channel partner"
                   onCancel={() => handleSuccessDialogClose()}
                   open={approveDialogOpen}
                   title="Verification Successful"
                   addInfo={`${channelPartner.id}: ${channelPartner.name}`}
                 />
                 <Button
                   color="success"
                   size="large"
                   variant="contained"
                   onClick={() => handleApprovalSubmit('Active')}
                   disabled={isApproveDisabled()}
                 >
                   Approve
                 </Button>
               </Grid>
             </Box>
           </Grid>
         </CardContent>
       </Card>
          )}
        </>
      )
      : <></>
  );
};

ApprovalCard.propTypes = {
  // @ts-ignore
  channelPartner: PropTypes.object,
  setLoading: PropTypes.func,
};
